/**
 * Etsy Integration Service
 * Handles OAuth 2.0 + PKCE flow with Etsy through the adapter service.
 *
 * Unlike Walmart (platform credentials), Etsy uses per-seller app credentials.
 * Sellers paste their own Keystring + Shared Secret, then OAuth into their shop.
 */

import axios from "axios";
import { walmartAdapterUrl } from "../constant";

// Adapter base URL (shared with Walmart — same service)
const ADAPTER_URL = walmartAdapterUrl;

// Skip ngrok browser warning for API calls
const adapterHeaders = { 'ngrok-skip-browser-warning': 'true' };

/**
 * Check Etsy connection status for a seller
 * @param {string|number} sellerId - companySellerId
 * @returns {Promise<{connected: boolean, connectedAt?: string}>}
 */
export const checkEtsyConnectionStatus = async (sellerId) => {
    try {
        const response = await axios.get(`${ADAPTER_URL}/auth/etsy/status/${sellerId}`, { headers: adapterHeaders });
        return {
            connected: response?.data?.data?.connected || false,
            connectedAt: response?.data?.data?.connectedAt || null,
            etsyUserId: response?.data?.data?.etsyUserId || null,
        };
    } catch (error) {
        console.error('Error checking Etsy connection status:', error);
        return { connected: false, connectedAt: null };
    }
};

/**
 * Initiate Etsy OAuth flow
 * Redirects to adapter which generates PKCE + redirects to Etsy login.
 *
 * @param {string} sellerId - companySellerId
 * @param {string} keystring - Seller's Etsy API Keystring
 * @param {string} sharedSecret - Seller's Etsy Shared Secret
 */
export const initiateEtsyOAuth = (companySellerId, keystring, sharedSecret, sellerId, companyId) => {
    if (!companySellerId) {
        throw new Error('Please select a Company/Seller before connecting to Etsy');
    }
    if (!keystring || !sharedSecret) {
        throw new Error('Etsy API Keystring and Shared Secret are required');
    }

    let authUrl = `${ADAPTER_URL}/auth/etsy/authorize?sellerId=${encodeURIComponent(companySellerId)}&keystring=${encodeURIComponent(keystring)}&sharedSecret=${encodeURIComponent(sharedSecret)}`;
    if (sellerId) authUrl += `&actualSellerId=${encodeURIComponent(sellerId)}`;
    if (companyId) authUrl += `&companyId=${encodeURIComponent(companyId)}`;

    console.log('Redirecting to Etsy OAuth for companySellerId:', companySellerId);
    window.location.href = authUrl;
};

/**
 * Disconnect Etsy account for a seller
 * @param {string|number} sellerId - companySellerId
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const disconnectEtsyAccount = async (sellerId) => {
    try {
        const response = await axios.delete(`${ADAPTER_URL}/auth/etsy/disconnect/${sellerId}`, { headers: adapterHeaders });
        return {
            success: response?.data?.success || false,
            message: response?.data?.data?.message || 'Etsy account disconnected',
        };
    } catch (error) {
        console.error('Error disconnecting Etsy:', error);
        return {
            success: false,
            message: error?.response?.data?.error?.message || 'Failed to disconnect Etsy account',
        };
    }
};
